import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export interface TurnstileWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
}

let turnstileScriptPromise: Promise<void> | null = null;

const TURNSTILE_SCRIPT_TIMEOUT_MS = 30_000;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  const pending = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]',
    ) as HTMLScriptElement | null;

    const settleFailure = (err: Error) => {
      reject(err);
    };

    if (existing) {
      const script = existing;
      if (window.turnstile) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onErr);
        if (window.turnstile) {
          resolve();
        } else {
          settleFailure(new Error('Turnstile script load timeout'));
        }
      }, TURNSTILE_SCRIPT_TIMEOUT_MS);

      function cleanup() {
        clearTimeout(timeout);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onErr);
      }

      function onLoad() {
        cleanup();
        if (window.turnstile) resolve();
        else settleFailure(new Error('Turnstile script loaded but API missing'));
      }

      function onErr() {
        cleanup();
        settleFailure(new Error('Turnstile script failed'));
      }

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onErr);

      // If the script finished loading before we subscribed, `load` will not fire again.
      queueMicrotask(() => {
        if (window.turnstile) {
          cleanup();
          resolve();
        }
      });
      return;
    }

    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => settleFailure(new Error('Turnstile script failed'));
    document.head.appendChild(s);
  });

  turnstileScriptPromise = pending;
  void pending.catch(() => {
    turnstileScriptPromise = null;
  });
  return pending;
}

/**
 * Cloudflare Turnstile (free CAPTCHA alternative). Requires `siteKey` from dashboard / admin payment settings.
 */
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  siteKey,
  onVerify,
  onExpire,
  theme = 'light',
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey?.trim() || !containerRef.current) return;
    let cancelled = false;

    void (async () => {
      try {
        await loadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey.trim(),
          theme,
          callback: (token: string) => onVerify(token),
          'expired-callback': () => onExpire?.(),
        });
      } catch {
        // parent may show error
      }
    })();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, onVerify, onExpire]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ minHeight: '70px' }}
      data-testid="turnstile-container"
    />
  );
};

export default TurnstileWidget;
